import React from "react";
import { Check, X, ShieldCheck } from "lucide-react";
import { track } from "../../lib/analytics";
import { withCalendlyTracking, setBookingContext } from "../../lib/attribution";
import { BookingButton } from "../BookingButton";
import { TrustpilotBadge } from "../TrustpilotBadge";
import { CALENDAR_URL_STANDARD } from "../../mock";
import { CHECKOUT_PAID_IN_FULL, CHECKOUT_MONTHLY, CTA_FULL, CTA_MONTHLY, PRICING, STORIES, STORIES_DISCLAIMER } from "../../lib/salesLetterCopy";

export const GOLD = "#D4AF35";

export const H2 = ({ children, id }) => (
  <h2 id={id} className="font-display font-black uppercase tracking-tight leading-[1.05] text-[26px] md:text-[34px] text-white mt-16 mb-6 scroll-mt-24">
    {children}
  </h2>
);

export const P = ({ children, className = "" }) => (
  <p className={`text-[17px] md:text-lg text-white/80 leading-[1.7] ${className}`}>{children}</p>
);

export const Callout = ({ children }) => (
  <p className="my-8 border-l-[3px] pl-5 py-1 font-display font-black uppercase tracking-tight text-xl md:text-2xl leading-snug text-white" style={{ borderColor: GOLD }}>
    {children}
  </p>
);

export const Blocks = ({ blocks }) => (
  <div className="space-y-5">
    {blocks.map((b, i) => {
      if (b.t === "h2") return <H2 key={i}>{b.text}</H2>;
      if (b.t === "callout") return <Callout key={i}>{b.text}</Callout>;
      if (b.t === "ol")
        return (
          <ol key={i} className="space-y-3 my-2" data-testid="letter-seven-systems">
            {b.items.map(([name, detail], n) => (
              <li key={name} className="flex gap-4 text-[17px] md:text-lg leading-relaxed">
                <span className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-display font-black text-sm text-[#0d0d0d]" style={{ background: GOLD }}>{n + 1}</span>
                <span className="text-white/80"><span className="text-white font-bold">{name}:</span> {detail}</span>
              </li>
            ))}
          </ol>
        );
      return <P key={i}>{b.text}</P>;
    })}
  </div>
);

const onCheckout = (plan, position) => () => track("letter_checkout_click", { plan, position, letter: "full_stack_coaching_v6" }, "InitiateCheckout");

export const CheckoutButtons = ({ position, fullSub = CTA_FULL.sub }) => (
  <div className="mt-8 flex flex-col gap-4" data-testid={`letter-checkout-${position}`}>
    <a href={CHECKOUT_PAID_IN_FULL} onClick={onCheckout("paid_in_full", position)} data-testid={`letter-cta-full-${position}`}
      className="group block rounded-2xl px-6 py-4 text-center text-[#0d0d0d] hover:brightness-110 active:scale-[0.99] transition shadow-[0_12px_40px_-8px_rgba(212,175,55,0.6)] focus-ring" style={{ background: GOLD }}>
      <span className="block font-display font-black uppercase tracking-wide text-base md:text-lg leading-tight">{CTA_FULL.label}</span>
      <span className="block mt-1 text-xs md:text-sm font-semibold text-[#0d0d0d]/75">{fullSub}</span>
    </a>
    <a href={CHECKOUT_MONTHLY} onClick={onCheckout("monthly", position)} data-testid={`letter-cta-monthly-${position}`}
      className="block rounded-2xl px-6 py-4 text-center border-2 text-white hover:bg-white/5 active:scale-[0.99] transition focus-ring" style={{ borderColor: GOLD }}>
      <span className="block font-display font-black uppercase tracking-wide text-base md:text-lg leading-tight" style={{ color: GOLD }}>{CTA_MONTHLY.label}</span>
      <span className="block mt-1 text-xs md:text-sm font-semibold text-white/70">{CTA_MONTHLY.sub}</span>
    </a>
  </div>
);

export const ConsultLink = ({ children, testid }) => (
  <BookingButton href={CALENDAR_URL_STANDARD} label={children} subline={null} compact testid={testid}
    onClick={() => track("letter_consult_click", { letter: "full_stack_coaching_v6" })}
    className="!inline-flex !rounded-full !px-5 !py-2 align-middle" />
);

// Inline text version for use mid-sentence (P.P.S.).
export const ConsultTextLink = ({ children, testid }) => {
  const href = withCalendlyTracking(CALENDAR_URL_STANDARD);
  const onClick = (e) => {
    track("letter_consult_click", { letter: "full_stack_coaching_v6" });
    setBookingContext({ location: testid });
    if (typeof window !== "undefined" && window.Calendly) {
      e.preventDefault();
      window.Calendly.initPopupWidget({ url: href });
    }
  };
  return (
    <a href={href} onClick={onClick} data-testid={testid} className="font-semibold underline underline-offset-4 decoration-2 hover:brightness-125 transition focus-ring rounded-sm" style={{ color: GOLD, textDecorationColor: GOLD }}>
      {children}
    </a>
  );
};

export const PricingTable = () => (
  <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] p-5 md:p-8" data-testid="letter-pricing">
    <p className="text-center text-[11px] md:text-xs font-bold tracking-[0.2em] uppercase" style={{ color: GOLD }}>{PRICING.eyebrow}</p>
    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
      {PRICING.tiers.map((t) => (
        <div key={t.label} className={`relative rounded-2xl p-5 md:p-6 text-center border ${t.best ? "border-[#D4AF35] bg-[#D4AF35]/10" : "border-white/10 bg-[#0d0d0d]"}`} data-testid={`letter-tier-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
          {t.best && <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-[10px] font-black tracking-[0.18em] uppercase text-[#0d0d0d]" style={{ background: GOLD }}>Best value</span>}
          <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-white/55">{t.label}</p>
          <p className={`mt-2 font-display font-black text-4xl md:text-[40px] leading-none ${t.best ? "" : "text-white"}`} style={t.best ? { color: GOLD } : undefined}>{t.price}</p>
          <p className="mt-3 text-sm text-white/65 leading-snug">{t.note}</p>
          {t.save && <p className="mt-3 text-sm font-bold" style={{ color: GOLD }}>{t.save}</p>}
        </div>
      ))}
    </div>
    <p className="mt-6 text-center text-sm md:text-base text-white/70 italic">{PRICING.urgency}</p>
    <CheckoutButtons position="pricing" />
  </div>
);

export const Stories = () => (
  <div className="mt-8 space-y-5" data-testid="letter-stories">
    {STORIES.map((s) => (
      <figure key={s.slug} className="flex gap-4 md:gap-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 md:p-6" data-testid={`letter-story-${s.slug}`}>
        <img src={`/success/photo-${s.slug}.jpg`} alt={s.name} loading="lazy" className="shrink-0 w-[88px] h-[110px] md:w-[120px] md:h-[150px] rounded-xl object-cover object-top" />
        <div className="min-w-0">
          <p className="text-[10px] md:text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: GOLD }}>{s.tag}</p>
          <blockquote className="mt-2 text-[15px] md:text-lg italic text-white/90 leading-relaxed">&ldquo;{s.quote}&rdquo;</blockquote>
          <figcaption className="mt-3 text-sm">
            <span className="block font-bold text-white">{s.name}</span>
            <span className="block text-white/55">{s.sub}</span>
          </figcaption>
          <p className="mt-3 text-sm text-white/70 leading-snug"><span className="font-bold text-white/85">The result:</span> {s.result}</p>
        </div>
      </figure>
    ))}
    <div className="pt-2 text-center">
      <p className="text-sm text-white/80 inline-flex items-center gap-2 flex-wrap justify-center">Rated &ldquo;Excellent&rdquo; on Trustpilot. <TrustpilotBadge /></p>
      <p className="mt-4 text-xs text-white/45 leading-relaxed max-w-[620px] mx-auto">{STORIES_DISCLAIMER}</p>
    </div>
  </div>
);

export const CheckList = ({ items, yes }) => (
  <ul className="space-y-3">
    {items.map((it) => (
      <li key={it} className="flex gap-3 text-[16px] md:text-[17px] text-white/80 leading-relaxed">
        <span className={`shrink-0 mt-1 w-6 h-6 rounded-full flex items-center justify-center ${yes ? "bg-[#D4AF35]/15" : "bg-white/10"}`} style={yes ? { color: GOLD } : { color: "rgba(255,255,255,.5)" }}>
          {yes ? <Check size={14} strokeWidth={3} /> : <X size={14} strokeWidth={3} />}
        </span>
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export const GuaranteeBadge = () => (
  <p className="mt-5 flex items-center justify-center gap-2 text-sm font-semibold text-white/75" data-testid="letter-guarantee-badge">
    <ShieldCheck size={18} style={{ color: GOLD }} /> Protected by the No More Wasted Time Guarantee.
  </p>
);
